import React from "react";
import dayjs from "dayjs";
import styled from "styled-components";
import { CheckOutlined, PlusOutlined } from "@ant-design/icons";
import { AppButton } from "@/shared/components";
import type { CampaignEngagementTerms } from "@/api/engagementTerms";

const Footer = styled.footer`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 16px;
	border-top: 1px solid #e6e6e6;
	background: #fefefe;
`;

const Updated = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 4px 12px;
	font-size: 12px;
	color: #707070;
`;
const AcceptedDot = styled.span`
	display: grid;
	place-items: center;
	width: 14px;
	height: 14px;
	margin-right: 1px;
	border-radius: 50%;
	background: rgba(27, 158, 90, 0.16);
	color: #1b9e5a;
	font-size: 9px;
`;

const Actions = styled.div`
	display: flex;
	gap: 8px;

	.ant-btn {
		width: 128px;
		height: 40px;
		border-radius: 64px;
		font-weight: 600;
	}

	.anticon {
		font-size: 16px;
	}
`;

interface Props {
	updatedAt: string | null;
	locked: boolean;
	dirty: boolean;
	loading: boolean;
	saving: boolean;
	submitting: boolean;
	canSend: boolean;
	onReset: () => void;
	onSave: () => void;
	onSend: () => void;
	creatorMode?: boolean;
	version?: CampaignEngagementTerms | null;
	isLatestVersion?: boolean;
	onIssueVersion?: () => void;
	unsavedVersion?: boolean;
}

export const EngagementTermsFooter: React.FC<Props> = ({
	updatedAt,
	locked,
	dirty,
	loading,
	saving,
	submitting,
	canSend,
	onReset,
	onSave,
	onSend,
	creatorMode = false,
	version = null,
	isLatestVersion = false,
	onIssueVersion,
	unsavedVersion = false,
}) => {
	const editorBusy = loading || saving || submitting;
	const accepted = creatorMode && version?.responseStatus === "accepted";
	const pending = creatorMode && version?.responseStatus === "pending";
	const canIssue =
		creatorMode &&
		isLatestVersion &&
		["pending", "accepted", "declined"].includes(version?.responseStatus || "") &&
		!!onIssueVersion;
	const changesDisabled = creatorMode ? (!dirty && !unsavedVersion) || editorBusy : !dirty || editorBusy;
	return (
		<Footer>
			<Updated>
				{accepted && (
					<AcceptedDot>
						<CheckOutlined />
					</AcceptedDot>
				)}
				{accepted ? (
					<>
						<span>Accepted by {version.respondedByName || "Creator"}</span>
						<span>
							{version.respondedAt ? dayjs(version.respondedAt).format("MMM DD, YYYY · h:mm A") : ""}
						</span>
					</>
				) : pending ? (
					`Sent to Creator${version?.presentedAt ? ` · ${dayjs(version.presentedAt).format("MMM DD, YYYY · h:mm A")}` : ""}`
				) : updatedAt ? (
					`Last updated: ${dayjs(updatedAt).format("MMM DD, YYYY · h:mm A")}`
				) : (
					"Not saved yet"
				)}
			</Updated>
			{canIssue && (
				<Actions>
					<AppButton variant="outline" onClick={onIssueVersion} disabled={editorBusy}>
						<PlusOutlined /> Issue new version
					</AppButton>
				</Actions>
			)}
			{!locked && (
				<Actions>
					<AppButton variant="outline" onClick={onReset} disabled={changesDisabled}>
						Cancel Changes
					</AppButton>
					<AppButton variant="primary" onClick={onSave} disabled={changesDisabled} style={{ width: "128px" }}>
						{saving ? "Saving..." : "Save"}
					</AppButton>
					{canSend && (
						<AppButton variant="primary" onClick={onSend} disabled={editorBusy}>
							Send to Creator
						</AppButton>
					)}
				</Actions>
			)}
		</Footer>
	);
};
